'use client'

import { DownloadIcon } from '@repo/ui/DownloadIcon'
import { EmptyState } from '@repo/ui/EmptyState'
import { FilterMenu } from '@repo/ui/FilterMenu'
import { LargeContactDetailsIcon } from '@repo/ui/LargeContactDetailsIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useDebounce } from '@uidotdev/usehooks'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/TextField'
import MUIRadio from '@mui/material/Radio'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const ContactsOrganisationsList = ({
  selectedItem,
  onSelect,
  mode = 'contacts',
  preSelectExpertTypeId,
  testData
}) => {
  const {
    fetchContactsBySearchTerm,
    fetchOrganisationsBySearchTerm,
    expertTypes,
    marketingMailingCategories,
    marketingIntroductionSources,
    fetchAllMarketingMailingCategories,
    fetchAllMarketingIntroductionSource,
    fetchAllContactsBySearchTermAsCSV,
    fetchAllOrganisationsBySearchTermAsCSV,
    refreshContactsTrigger,
    refreshOrganisationsTrigger
  } = useLookupsStore()
  const [listItems, setListItems] = useState(null)
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [isPaginating, setIsPaginating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 400)
  const [filters, setFilters] = useState({
    expertTypeId: preSelectExpertTypeId || null,
    marketingMailingCategoryId: null,
    marketingIntroductionSourceId: null,
    hasOptedInToEmail: null
  })
  const [startAtIndex, setStartAtIndex] = useState(0)
  const debouncedStartAtIndex = useDebounce(startAtIndex, 400)
  const [pageSize] = useState(50)
  const listItemsRef = useRef(null)
  const { palette } = useTheme()

  useEffect(() => {
    if (testData) return
    if (!marketingMailingCategories?.length) fetchAllMarketingMailingCategories()
    if (!marketingIntroductionSources?.length) fetchAllMarketingIntroductionSource()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleScroll = (e) => {
      const hasMoreResults = listItems ? listItems.length < totalRowCount : false
      if (!hasMoreResults) return

      const isAtBottom = e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight
      if (!isAtBottom) return

      const newStartAtIndex = startAtIndex + pageSize
      setStartAtIndex(newStartAtIndex)
      // changing this value will trigger a new fetch via useEffect
    }

    const scrollPanel = listItemsRef?.current || null

    if (scrollPanel) {
      scrollPanel.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollPanel) {
        scrollPanel.removeEventListener('scroll', handleScroll)
      }
    }
  }, [listItemsRef, startAtIndex, pageSize, totalRowCount, listItems])

  const fetchData = useCallback(async () => {
    if (testData) return

    if (!listItems) setIsFetching(true)
    if (debouncedStartAtIndex && debouncedStartAtIndex > 0) setIsPaginating(true)

    const params = {
      searchTerm: debouncedSearchTerm,
      expertTypeId: filters.expertTypeId,
      marketingMailingCategoryId: filters.marketingMailingCategoryId,
      marketingIntroductionSourceId: filters.marketingIntroductionSourceId,
      hasOptedInToEmail: filters.hasOptedInToEmail,
      startAtIndex: debouncedStartAtIndex,
      pageSize,
      hasEmail: false
    }

    let newListItems = []
    let newTotalRowCount = 0

    if (mode === 'contacts') {
      let { rows, totalRowCount } = await fetchContactsBySearchTerm(params)
      newListItems = [...rows]
      newTotalRowCount = totalRowCount
    } else if (mode === 'organisations') {
      let { rows, totalRowCount } = await fetchOrganisationsBySearchTerm(params)
      newListItems = [...rows]
      newTotalRowCount = totalRowCount
    }

    if (startAtIndex !== 0) {
      // if we're getting a new page of items, append to the list rather than replacing
      newListItems = [...listItems, ...newListItems]
    }

    setListItems(newListItems)
    setTotalRowCount(newTotalRowCount)
    setIsFetching(false)
    setIsPaginating(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchContactsBySearchTerm, open, debouncedSearchTerm, filters, debouncedStartAtIndex, pageSize])

  useEffect(() => {
    fetchData()
  }, [debouncedSearchTerm, filters, debouncedStartAtIndex, pageSize, fetchData])

  useEffect(() => {
    setStartAtIndex(0)
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshContactsTrigger, refreshOrganisationsTrigger])

  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm || '')
    setStartAtIndex(0)
    // changing this value will trigger a new fetch via useEffect
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setStartAtIndex(0)
    // changing filters will trigger a new fetch via useEffect
  }

  const handleDownloadCSV = async () => {
    const params = {
      searchTerm: debouncedSearchTerm,
      expertTypeId: filters.expertTypeId,
      marketingMailingCategoryId: filters.marketingMailingCategoryId,
      marketingIntroductionSourceId: filters.marketingIntroductionSourceId,
      hasOptedInToEmail: filters.hasOptedInToEmail,
      hasEmail: false
    }

    if (mode === 'contacts') {
      await fetchAllContactsBySearchTermAsCSV(params)
    } else if (mode === 'organisations') {
      await fetchAllOrganisationsBySearchTermAsCSV(params)
    }
  }

  const filterOptions = [
    {
      key: 'expertTypeId',
      title: 'Expert type',
      items: expertTypes?.map((expertType) => ({
        label: expertType.description,
        value: expertType.expertTypeId
      }))
    },
    {
      key: 'marketingMailingCategoryId',
      title: 'Marketing mailing category',
      items: marketingMailingCategories?.map((category) => ({
        label: category.description,
        value: category.marketingMailingCategoryId
      }))
    },
    {
      key: 'marketingIntroductionSourceId',
      title: 'Introduction source',
      items: marketingIntroductionSources?.map((source) => ({
        label: source.description,
        value: source.marketingIntroductionSourceId
      }))
    },
    {
      key: 'hasOptedInToEmail',
      title: 'Opted in to email',
      type: 'boolean'
    }
  ]

  return (
    <MUIStack
      data-testid="ContactsOrganisationsList"
      direction="column"
      justifyContent="flex-start"
      sx={{ flex: 1, maxWidth: '400px', height: '100%', overflowY: 'auto', position: 'relative' }}
    >
      <MUIStack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <MUIInput
          label="Search"
          value={searchTerm}
          size="small"
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ mt: 2, mb: 2, flex: 1 }}
        />
        <FilterMenu
          onFilterChange={handleFilterChange}
          options={filterOptions}
          defaultValues={{ expertTypeId: preSelectExpertTypeId || null }}
        />
        <MUITooltip title="Download as CSV" arrow placement="top">
          <MUIIconButton onClick={handleDownloadCSV}>
            <DownloadIcon sx={{ width: '22px', height: '22px', color: palette?.greyShades?.grey3 }} />
          </MUIIconButton>
        </MUITooltip>
      </MUIStack>
      <MUIStack direction="column" sx={{ flex: 1, overflowY: 'auto' }} ref={listItemsRef}>
        {isFetching ? (
          <LoadingState sx={{ mt: 10 }} />
        ) : listItems && !listItems.length ? (
          <EmptyState
            sx={{ mt: 10 }}
            icon={<LargeContactDetailsIcon sx={{ mb: 8, width: '64px', height: '64px' }} />}
          />
        ) : (
          <>
            {listItems?.map((item, index) => (
              <ListItem
                key={`$item.contactId || item.organisationId}-${index}`}
                item={item}
                selectedItem={selectedItem}
                onSelect={onSelect}
                mode={mode}
              />
            ))}
          </>
        )}
      </MUIStack>

      {isPaginating ? (
        <MUIStack
          sx={{
            zIndex: 10,
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '60px',
            backgroundColor: 'white',
            borderRadius: '6px'
          }}
          alignItems="center"
          justifyContent="center"
        >
          <LoadingState sx={{ width: '40px', height: '40px' }} />
        </MUIStack>
      ) : null}
    </MUIStack>
  )
}

ContactsOrganisationsList.propTypes = {
  selectedItem: PropTypes.any,
  onSelect: PropTypes.func,
  mode: PropTypes.oneOf(['contacts', 'organisations']),
  preSelectExpertTypeId: PropTypes.any,
  testData: PropTypes.any
}

const ListItem = ({ item, selectedItem, onSelect, mode }) => {
  const { expertTypesById } = useLookupsStore()

  const renderName = (item) => {
    if (!item) return ''
    const elements = [item.title, item.forename, item.surname, item.name]
    return elements.filter(Boolean).join(' ')
  }

  const isSelected =
    mode === 'contacts'
      ? item?.contactId === selectedItem?.contactId
      : item?.organisationId === selectedItem?.organisationId
  const expertType = expertTypesById && item?.expertTypeId ? expertTypesById[item.expertTypeId] : ''

  return (
    <MUIStack
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      gap={1}
      onClick={onSelect ? () => onSelect(item) : null}
      sx={{
        backgroundColor: isSelected ? 'rgba(80, 115, 195, 0.12)' : 'rgba(80, 115, 195, 0.06)',
        borderRadius: '4px',
        mb: 1,
        width: '100%',
        cursor: 'pointer',
        padding: '6px'
      }}
    >
      <MUIRadio sx={{ pointerEvents: 'none' }} checked={isSelected} />
      <MUIStack direction="column">
        <MUITypography variant="body1" sx={{ pointerEvents: 'none', mb: 0, flex: 1, flexDirection: 'column' }} noWrap>
          {renderName(item)}
        </MUITypography>

        {expertType?.description ? (
          <MUITypography variant="body2" sx={{ pointerEvents: 'none', mb: 0, color: 'text.secondary' }} noWrap>
            {expertType.description}
          </MUITypography>
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}

ListItem.propTypes = {
  item: PropTypes.object,
  selectedItem: PropTypes.object,
  onSelect: PropTypes.func,
  mode: PropTypes.oneOf(['contacts', 'organisations']),
  testData: PropTypes.any
}
